import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getOperationalCases, createOperationalCase, updateOperationalCase, updateCaseCheck,
  deleteOperationalCase, getClients, exportControlMensual
} from '../lib/api';
import { getStoredToken } from '../lib/authStorage';
import { movementTypeLabels, checkStatusLabels, plans, clientOrigins, getTodayDate } from '../lib/utils';
import { useAllPlans } from '../lib/usePlans';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Plus, Search, Loader2, CheckCircle, Clock, FileText,
  AlertTriangle, Filter, Trash2, Calendar, ChevronLeft, ChevronRight, Download
} from 'lucide-react';
import { toast } from 'sonner';

const actionTypes = [
  { value: 'nuevo_cliente', label: 'Nuevo Cliente' },
  { value: 'cancelacion', label: 'Cancelación' },
  { value: 'congelacion', label: 'Congelación' },
  { value: 'descongelacion', label: 'Descongelación' },
  { value: 'cambio_plan', label: 'Cambio de Plan' },
  { value: 'renovacion', label: 'Renovación' },
];

const checkFields = [
  { key: 'box_magic_status', label: 'Box Magic' },
  { key: 'virtualpos_status', label: 'Virtual POS' },
  { key: 'mail_status', label: 'Mail' },
  { key: 'boleta_status', label: 'Boleta' },
  { key: 'estado_final', label: 'Estado Final' },
  { key: 'structured_status', label: 'Structured' },
];

const checkOptions = [
  { value: 'ok', label: 'OK' },
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'no_aplica', label: 'N/A' },
  { value: 'enviado', label: 'Enviado' },
];

const CheckBadge = ({ status }) => {
  const colors = {
    ok: 'bg-green-100 text-green-700',
    pendiente: 'bg-amber-100 text-amber-700',
    no_aplica: 'bg-gray-100 text-gray-500',
    enviado: 'bg-blue-100 text-blue-700',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors[status] || 'bg-gray-100 text-gray-500'}`}>
      {checkStatusLabels[status] || status || '-'}
    </span>
  );
};

const getActionColor = (tipo) => {
  const colors = {
    nuevo_cliente: 'bg-green-100 text-green-700',
    cancelacion: 'bg-red-100 text-red-700',
    congelacion: 'bg-blue-100 text-blue-700',
    descongelacion: 'bg-cyan-100 text-cyan-700',
    cambio_plan: 'bg-purple-100 text-purple-700',
    renovacion: 'bg-emerald-100 text-emerald-700',
  };
  return colors[tipo] || 'bg-gray-100 text-gray-600';
};

const CasosOperativos = () => {
  const { isDirection } = useAuth();
  const allPlans = useAllPlans();
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateMode, setDateMode] = useState('mes'); // 'mes' | 'rango' | 'todo'
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    tipo_accion: 'nuevo_cliente',
    client_name: '',
    client_id: '',
    plan: '',
    email: '',
    phone: '',
    rut: '',
    origen: 'manual',
    fecha_accion: getTodayDate(),
    fecha_fin_plan: '',
    observaciones: '',
  });

  const getDateFilters = useCallback(() => {
    if (dateMode === 'mes' && selectedMonth) {
      const [y, m] = selectedMonth.split('-');
      const lastDay = new Date(parseInt(y), parseInt(m), 0).getDate();
      return { fecha_desde: `${selectedMonth}-01`, fecha_hasta: `${selectedMonth}-${lastDay}` };
    }
    if (dateMode === 'rango') {
      return { fecha_desde: fechaDesde || undefined, fecha_hasta: fechaHasta || undefined };
    }
    return {};
  }, [dateMode, selectedMonth, fechaDesde, fechaHasta]);

  const fetchCases = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.tipo_accion = typeFilter;
      if (search) params.search = search;
      const dateParams = getDateFilters();
      if (dateParams.fecha_desde) params.fecha_desde = dateParams.fecha_desde;
      if (dateParams.fecha_hasta) params.fecha_hasta = dateParams.fecha_hasta;
      const res = await getOperationalCases(params);
      setCases(res.data);
    } catch (error) {
      toast.error('Error al cargar control mensual');
    } finally { setLoading(false); }
  }, [statusFilter, typeFilter, search, getDateFilters]);

  useEffect(() => { fetchCases(); }, [fetchCases]);

  const navigateMonth = (direction) => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + direction, 1);
    setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const monthLabel = (monthStr) => {
    const [y, m] = monthStr.split('-');
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return `${months[parseInt(m) - 1]} ${y}`;
  };

  const fetchClients = async () => {
    try {
      const res = await getClients({});
      setClients(res.data);
    } catch (e) { 
      
    }
  };

  const handleSearch = () => {
    setLoading(true);
    fetchCases();
  };

  const isNewClientAction = formData.tipo_accion === 'nuevo_cliente';

  const handleCreate = async (e) => {
    e.preventDefault();
    if (isNewClientAction && !formData.client_name.trim()) {
      toast.error('Nombre de cliente requerido');
      return;
    }
    if (!isNewClientAction && !formData.client_id) {
      toast.error('Debes seleccionar un cliente existente');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        tipo_accion: formData.tipo_accion,
        client_name: formData.client_name,
        client_id: formData.client_id || null,
        plan: formData.plan,
        origen: formData.origen,
        fecha_accion: formData.fecha_accion,
        observaciones: formData.observaciones,
      };
      // For new client, include extra fields
      if (isNewClientAction && !formData.client_id) {
        payload.email = formData.email;
        payload.phone = formData.phone;
        payload.rut = formData.rut;
        payload.fecha_fin_plan = formData.fecha_fin_plan || null;
      }
      await createOperationalCase(payload);
      toast.success(isNewClientAction
        ? 'Caso creado y cliente registrado'
        : 'Caso operativo registrado');
      setShowNewDialog(false);
      resetForm();
      fetchCases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear caso');
    } finally { setSaving(false); }
  };

  const resetForm = () => {
    setFormData({
      tipo_accion: 'nuevo_cliente', client_name: '', client_id: '', plan: '',
      email: '', phone: '', rut: '',
      origen: 'manual', fecha_accion: new Date().toISOString().split('T')[0], fecha_fin_plan: '', observaciones: '',
    });
  };

  const handleCheckUpdate = async (caseId, field, value) => {
    try {
      const updated = await updateCaseCheck(caseId, { [field]: value });
      setCases(prev => prev.map(c => c.id === caseId ? updated.data : c));
      if (selectedCase?.id === caseId) setSelectedCase(updated.data);
      toast.success('Check actualizado');
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleCloseCase = async (caseId) => {
    try {
      const updated = await updateOperationalCase(caseId, { status: 'cerrado' });
      setCases(prev => prev.map(c => c.id === caseId ? updated.data : c));
      if (selectedCase?.id === caseId) setSelectedCase(updated.data);
      toast.success('Caso cerrado');
    } catch (error) {
      toast.error('Error al cerrar caso');
    }
  };

  const handleDeleteCase = async (e, caseItem) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminar caso de "${caseItem.client_name}"? Esta accion no se puede deshacer.`)) return;
    try {
      await deleteOperationalCase(caseItem.id);
      toast.success('Caso eliminado');
      fetchCases();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const openDetail = (c) => {
    setSelectedCase(c);
    setShowDetailDialog(true);
  };

  const openNewDialog = () => {
    fetchClients();
    resetForm();
    setShowNewDialog(true);
  };

  const handleExportCM = async () => {
    const dateParams = getDateFilters();
    const url = exportControlMensual(dateParams.fecha_desde, dateParams.fecha_hasta);
    const token = getStoredToken();
    try {
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error('Error');
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `control_mensual.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Descarga completada');
    } catch (e) {
      toast.error('Error al descargar. Solo Direccion puede exportar.');
    }
  };

  const handleClientSelect = (clientId) => {
    const cl = clients.find(c => c.id === clientId);
    if (cl) {
      setFormData(prev => ({
        ...prev,
        client_id: clientId,
        client_name: cl.name,
        plan: cl.current_plan || prev.plan,
        origen: cl.origen || prev.origen,
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="cases-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#2B4257]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="control-mensual-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title">Control Mensual</h1>
          <p className="page-subtitle">Control de altas, bajas, congelaciones y cambios de plan</p>
        </div>
        <div className="flex gap-2">
          {isDirection() && (
            <Button className="btn-secondary gap-2" onClick={handleExportCM} data-testid="btn-export-cm">
              <Download size={16} /> Exportar Excel
            </Button>
          )}
          <Button className="btn-primary gap-2" onClick={openNewDialog} data-testid="btn-new-case">
            <Plus size={18} /> Nuevo Registro
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="card space-y-3">
        {/* Row 1: Search + Type + Status */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C7083]" size={16} />
            <Input
              value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Buscar por nombre de cliente..."
              className="pl-10" data-testid="input-search-cases"
            />
          </div>
          <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[180px]" data-testid="filter-type">
              <Filter size={14} className="mr-2" />
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {actionTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[150px]" data-testid="filter-status">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="abierto">Abierto</SelectItem>
              <SelectItem value="cerrado">Cerrado</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Row 2: Date Filters */}
        <div className="flex flex-col md:flex-row items-start md:items-center gap-3 pt-2 border-t border-[#E8E2D7]">
          <div className="flex items-center gap-1">
            <Calendar size={16} className="text-[#5C7083]" />
            <span className="text-sm text-[#5C7083] mr-1">Fecha:</span>
            <div className="flex bg-[#F5F0E8] rounded-lg p-0.5">
              <button onClick={() => setDateMode('mes')}
                className={`text-xs px-3 py-1.5 rounded-md transition-colors ${dateMode === 'mes' ? 'bg-[#2B4257] text-white' : 'text-[#5C7083] hover:text-[#1A2835]'}`}
                data-testid="btn-date-mode-mes">Mes</button>
              <button onClick={() => setDateMode('rango')}
                className={`text-xs px-3 py-1.5 rounded-md transition-colors ${dateMode === 'rango' ? 'bg-[#2B4257] text-white' : 'text-[#5C7083] hover:text-[#1A2835]'}`}
                data-testid="btn-date-mode-rango">Rango</button>
              <button onClick={() => { setDateMode('todo'); setFechaDesde(''); setFechaHasta(''); }}
                className={`text-xs px-3 py-1.5 rounded-md transition-colors ${dateMode === 'todo' ? 'bg-[#2B4257] text-white' : 'text-[#5C7083] hover:text-[#1A2835]'}`}
                data-testid="btn-date-mode-todo">Todo</button>
            </div>
          </div>

          {dateMode === 'mes' && (
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" className="p-1 h-8 w-8" onClick={() => navigateMonth(-1)} data-testid="btn-prev-month">
                <ChevronLeft size={16} />
              </Button>
              <span className="text-sm font-medium text-[#1A2835] min-w-[140px] text-center" data-testid="label-current-month">
                {monthLabel(selectedMonth)}
              </span>
              <Button variant="ghost" size="sm" className="p-1 h-8 w-8" onClick={() => navigateMonth(1)} data-testid="btn-next-month">
                <ChevronRight size={16} />
              </Button>
              <Input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-auto text-xs h-8" data-testid="input-month-picker" />
            </div>
          )}

          {dateMode === 'rango' && (
            <div className="flex items-center gap-2">
              <Input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)}
                className="w-auto text-xs h-8" data-testid="input-fecha-desde" placeholder="Desde" />
              <span className="text-xs text-[#5C7083]">a</span>
              <Input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)}
                className="w-auto text-xs h-8" data-testid="input-fecha-hasta" placeholder="Hasta" />
            </div>
          )}

          <span className="text-xs text-[#5C7083] ml-auto" data-testid="cases-count">
            {cases.length} registros
          </span>
        </div>
      </div>

      {/* Cases Table */}
      <div className="card" data-testid="cases-table">
        {cases.length === 0 ? (
          <div className="text-center py-12 text-[#5C7083]">
            <FileText className="mx-auto h-16 w-16 text-[#E8E2D7] mb-4" />
            <p>No hay registros en el control mensual</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table w-full text-sm">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Cliente</th>
                  <th>Plan</th>
                  <th>BM</th>
                  <th>VPos</th>
                  <th>Mail</th>
                  <th>Boleta</th>
                  <th>E.Final</th>
                  <th>Struct.</th>
                  <th>Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {cases.map((c) => (
                  <tr key={c.id} className="cursor-pointer hover:bg-[#F5F0E8]/50" onClick={() => openDetail(c)} data-testid={`case-row-${c.id}`}>
                    <td className="whitespace-nowrap">{c.fecha_accion}</td>
                    <td>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getActionColor(c.tipo_accion)}`}>
                        {movementTypeLabels[c.tipo_accion] || c.tipo_accion}
                      </span>
                    </td>
                    <td className="font-medium">{c.client_name}</td>
                    <td className="text-[#5C7083]">{c.plan || '-'}</td>
                    <td><CheckBadge status={c.box_magic_status} /></td>
                    <td><CheckBadge status={c.virtualpos_status} /></td>
                    <td><CheckBadge status={c.mail_status} /></td>
                    <td><CheckBadge status={c.boleta_status} /></td>
                    <td><CheckBadge status={c.estado_final} /></td>
                    <td><CheckBadge status={c.structured_status} /></td>
                    <td>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.status === 'cerrado' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        {c.status === 'cerrado' ? 'Cerrado' : 'Abierto'}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        {c.requires_virtualpos_action && !c.virtualpos_action_completed && (
                          <AlertTriangle size={14} className="text-amber-500" title="Requiere accion VPos" />
                        )}
                        {isDirection() && (
                          <button
                            onClick={(e) => handleDeleteCase(e, c)}
                            className="p-1 text-[#C25953] hover:text-[#A63D3D] hover:bg-[#FBEAE9] rounded transition-colors"
                            data-testid={`btn-delete-case-${c.id}`}
                            title="Eliminar caso">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* New Case Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nuevo Registro - Control Mensual</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4" data-testid="new-case-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Tipo de Accion *</Label>
                <Select value={formData.tipo_accion} onValueChange={(v) => {
                  setFormData({ ...formData, tipo_accion: v, client_id: '', client_name: '' });
                }}>
                  <SelectTrigger data-testid="select-case-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {actionTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Fecha *</Label>
                <Input type="date" value={formData.fecha_accion}
                  onChange={(e) => setFormData({ ...formData, fecha_accion: e.target.value })}
                  required data-testid="input-case-date" />
              </div>
            </div>

            {/* For existing client actions: show dropdown */}
            {!isNewClientAction && (
              <div>
                <Label className="form-label">Seleccionar Cliente Existente *</Label>
                <Select value={formData.client_id || undefined} onValueChange={handleClientSelect}>
                  <SelectTrigger data-testid="select-existing-client">
                    <SelectValue placeholder="Buscar cliente..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(cl => (
                      <SelectItem key={cl.id} value={cl.id}>
                        {cl.name} {cl.current_plan ? `- ${cl.current_plan}` : ''} {cl.origen ? `(${cl.origen})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* For new client: show full form */}
            {isNewClientAction && (
              <>
                <div>
                  <Label className="form-label">Nombre Cliente *</Label>
                  <div className="space-y-2">
                    {clients.length > 0 && (
                      <Select value={formData.client_id || "new"}
                        onValueChange={(v) => {
                          if (v === 'new') {
                            setFormData(prev => ({ ...prev, client_id: '', client_name: '' }));
                          } else {
                            handleClientSelect(v);
                          }
                        }}>
                        <SelectTrigger data-testid="select-or-new-client">
                          <SelectValue placeholder="Seleccionar existente o crear nuevo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">+ Crear cliente nuevo</SelectItem>
                          {clients.map(cl => (
                            <SelectItem key={cl.id} value={cl.id}>{cl.name} {cl.current_plan ? `(${cl.current_plan})` : ''}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {!formData.client_id && (
                      <Input value={formData.client_name}
                        onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                        placeholder="Nombre completo del nuevo cliente" required
                        data-testid="input-new-client-name" />
                    )}
                  </div>
                </div>
                {!formData.client_id && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="form-label">Email</Label>
                      <Input type="email" value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="email@ejemplo.com" data-testid="input-new-client-email" />
                    </div>
                    <div>
                      <Label className="form-label">Telefono</Label>
                      <Input value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+56 9 ..." data-testid="input-new-client-phone" />
                    </div>
                    <div>
                      <Label className="form-label">RUT</Label>
                      <Input value={formData.rut}
                        onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
                        placeholder="12.345.678-9" data-testid="input-new-client-rut" />
                    </div>
                    <div>
                      <Label className="form-label">Origen</Label>
                      <Select value={formData.origen} onValueChange={(v) => setFormData({ ...formData, origen: v })}>
                        <SelectTrigger data-testid="select-case-origin"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {clientOrigins.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label className="form-label">Fin Plan (Hasta)</Label>
                      <Input type="date" value={formData.fecha_fin_plan}
                        onChange={(e) => setFormData({ ...formData, fecha_fin_plan: e.target.value })}
                        data-testid="input-new-client-fecha-fin" />
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Plan</Label>
                <Select value={formData.plan || "none"}
                  onValueChange={(v) => setFormData({ ...formData, plan: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="select-case-plan"><SelectValue placeholder="Plan" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-</SelectItem>
                    {allPlans.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {isNewClientAction && formData.client_id && (
                <div>
                  <Label className="form-label">Origen</Label>
                  <Select value={formData.origen} onValueChange={(v) => setFormData({ ...formData, origen: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {clientOrigins.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div>
              <Label className="form-label">Observaciones</Label>
              <textarea value={formData.observaciones}
                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                className="form-input min-h-[60px] resize-none w-full" placeholder="Notas adicionales..."
                data-testid="input-case-obs" />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowNewDialog(false)} className="btn-secondary">Cancelar</Button>
              <Button type="submit" className="btn-primary" disabled={saving} data-testid="btn-confirm-case">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {isNewClientAction ? 'Crear Caso + Cliente' : 'Registrar Caso'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedCase?.client_name}
              <span className={`text-xs px-2 py-0.5 rounded-full ${getActionColor(selectedCase?.tipo_accion)}`}>
                {movementTypeLabels[selectedCase?.tipo_accion]}
              </span>
            </DialogTitle>
          </DialogHeader>
          {selectedCase && (
            <div className="space-y-4" data-testid="case-detail">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div><span className="text-[#5C7083]">Fecha:</span> <span className="font-medium ml-1">{selectedCase.fecha_accion}</span></div>
                <div><span className="text-[#5C7083]">Plan:</span> <span className="font-medium ml-1">{selectedCase.plan || '-'}</span></div>
                <div><span className="text-[#5C7083]">Origen:</span> <span className="font-medium ml-1">{selectedCase.origen}</span></div>
                <div><span className="text-[#5C7083]">Registrado por:</span> <span className="font-medium ml-1">{selectedCase.registered_by_name}</span></div>
                <div>
                  <span className="text-[#5C7083]">Estado:</span>
                  <span className={`ml-1 text-xs px-2 py-0.5 rounded-full font-medium ${selectedCase.status === 'cerrado' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {selectedCase.status === 'cerrado' ? 'Cerrado' : 'Abierto'}
                  </span>
                </div>
                {selectedCase.requires_virtualpos_action && (
                  <div className="flex items-center gap-1">
                    <AlertTriangle size={14} className="text-amber-500" />
                    <span className="text-sm">VPos: {selectedCase.virtualpos_action_completed ? 'Completado' : 'Pendiente'}</span>
                  </div>
                )}
              </div>

              {selectedCase.observaciones && (
                <div className="p-3 bg-[#F5F0E8] rounded-lg text-sm">
                  <span className="text-[#5C7083]">Observaciones:</span> {selectedCase.observaciones}
                </div>
              )}

              {/* Check fields */}
              <div>
                <h4 className="font-semibold text-[#1A2835] mb-3">Checks</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {checkFields.map(f => (
                    <div key={f.key} className="flex items-center justify-between p-2 bg-[#FAFAF5] rounded-lg">
                      <span className="text-sm text-[#5C7083]">{f.label}</span>
                      <Select
                        value={selectedCase[f.key] || 'pendiente'}
                        onValueChange={(v) => handleCheckUpdate(selectedCase.id, f.key, v)}
                      >
                        <SelectTrigger className="w-[100px] h-7 text-xs" data-testid={`check-${f.key}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {checkOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>

              {selectedCase.status !== 'cerrado' && (
                <div className="flex justify-end">
                  <Button className="btn-primary gap-2" onClick={() => handleCloseCase(selectedCase.id)} data-testid="btn-close-case">
                    <CheckCircle size={16} /> Cerrar Caso
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CasosOperativos;
